/**
 * useAsset Hook
 * 
 * Custom React hook for working with whowset assets.
 * Provides reactive asset URL resolution with theme/language support.
 * Automatically injects current theme from ThemeContext.
 */

import { useState, useEffect, useSyncExternalStore } from 'react'
import { assetLoader } from '../services/assetLoader'
import { useTheme } from '../contexts/ThemeContext'
import type { AssetOptions, ResolvedAsset } from '../types/whowset'

/**
 * Hook to get an asset URL
 * Automatically uses current theme from context
 * @param assetPath - The asset path from whowset
 * @param options - Theme/language options (theme is auto-injected if not provided)
 * @returns Asset URL or null
 */
export function useAssetUrl(assetPath: string | null, options: AssetOptions = {}): string | null {
    const { themeName } = useTheme()
    const ready = useSyncExternalStore(assetLoader.subscribe, assetLoader.getSnapshot, () => false)

    if (!ready || !assetPath) {
        return null
    }

    return assetLoader.getAssetUrl(assetPath, {
        ...options,
        theme: options.theme ?? (themeName === 'generic' ? 'default' : themeName),
    })
}

/**
 * Hook to get full asset information
 * Automatically uses current theme from context
 * @param assetPath - The asset path from whowset
 * @param options - Theme/language options (theme is auto-injected if not provided)
 * @returns Resolved asset information or null
 */
export function useAsset(assetPath: string, options: AssetOptions = {}): ResolvedAsset | null {
    const { themeName } = useTheme()
    // Depend on individual values because callers may pass a fresh options object.
    const { theme, language, fallbackTheme, fallbackLanguage } = options
    const [asset, setAsset] = useState<ResolvedAsset | null>(null)

    useEffect(() => {
        // Merge current theme with provided options (provided theme takes precedence)
        const mergedOptions: AssetOptions = {
            theme: theme ?? (themeName === 'generic' ? 'default' : themeName),
            language,
            fallbackTheme,
            fallbackLanguage,
        }
        const resolvedAsset = assetLoader.getAsset(assetPath, mergedOptions)
        setAsset(resolvedAsset)
    }, [assetPath, themeName, theme, language, fallbackTheme, fallbackLanguage])

    return asset
}

/**
 * Hook to search for assets by pattern
 * @param pattern - Search pattern (string or RegExp)
 * @returns Array of matching asset paths
 */
export function useAssetSearch(pattern: string | RegExp): string[] {
    const [results, setResults] = useState<string[]>([])

    useEffect(() => {
        const searchResults = assetLoader.searchAssets(pattern)
        setResults(searchResults)
    }, [pattern])

    return results
}

